/**
 * Session provider — roadmap 4.1.
 *
 * One `GET /me/` behind TanStack Query, shared by every consumer. The Phase 1
 * localStorage stub is gone: roles now come from `RoleAssignment` on the server,
 * which is the only place they were ever authoritative (§3.4).
 *
 * A 401 here is not an error to retry or report. An anonymous visitor on the
 * public catalogue is a normal, expected state, and treating it as a failure
 * would put an error boundary in front of the home page.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo } from 'react'

import { api, setUnauthenticatedHandler, unwrap } from '@/shared/api/client'
import { isApiError } from '@/shared/api/problem'
import { queryKeys } from '@/shared/api/queryKeys'
import type { Me } from '@/shared/api/types'

import { isRole, type Role } from './roles'
import { SessionContext, type Session } from './SessionContext'

/** `null` for an anonymous caller, rather than a thrown 401. */
async function fetchMe(): Promise<Me | null> {
  try {
    return await unwrap(api.GET('/api/v1/me/'))
  } catch (error) {
    if (isApiError(error) && error.status === 401) return null
    throw error
  }
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()

  const { data, status, refetch } = useQuery({
    queryKey: queryKeys.me,
    queryFn: fetchMe,
    // The session is read on every screen; refetching it per mount would put a
    // request on every navigation for data that changes on login and logout.
    staleTime: 5 * 60_000,
    // A 401 already resolved to `null` above, so a rejection here is a network
    // or server fault — worth one retry, not five.
    retry: 1,
  })

  // When the client gives up on refreshing (`client.ts`), the session is over.
  // Writing `null` into the cache is what turns that into an `anonymous`
  // status, which is what makes the route guards redirect.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      queryClient.setQueryData(queryKeys.me, null)
    })
    return () => setUnauthenticatedHandler(() => {})
  }, [queryClient])

  const refresh = useCallback(async () => {
    await refetch()
  }, [refetch])

  const value = useMemo<Session>(() => {
    const user = data ?? null
    const roles: Role[] = (user?.roles ?? []).filter((role): role is Role => isRole(role))

    return {
      status: status === 'pending' ? 'loading' : user ? 'authenticated' : 'anonymous',
      user,
      hasRole: (role: Role) => roles.includes(role),
      isVerified: user?.is_verified ?? false,
      refresh,
    }
  }, [data, status, refresh])

  return <SessionContext value={value}>{children}</SessionContext>
}
